import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Save } from "lucide-react";
import { api, formatErr } from "../lib/api";

export default function PricingAdmin() {
    const [tiers, setTiers] = useState(null);
    const [projects, setProjects] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: t }, { data: p }, { data: l }] = await Promise.all([
                api.get("/admin/cpl/tiers"),
                api.get("/admin/cpl/projects"),
                api.get("/admin/cpl/logs?limit=50"),
            ]);
            setTiers(t); setProjects(p); setLogs(l);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveProject = async (p) => {
        const patch = edits[p.id];
        if (!patch) return;
        try {
            await api.patch(`/admin/cpl/projects/${p.id}`, patch);
            toast.success(`${p.project_name} updated`);
            setEdits((e) => ({ ...e, [p.id]: undefined }));
            load();
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-8" data-testid="pricing-admin">
            {/* Pending sign-off */}
            {tiers?.pending_signoff?.length > 0 && (
                <div className="border border-yellow-500/30 bg-yellow-500/5 p-4">
                    <div className="flex items-center gap-2 text-yellow-400 text-xs uppercase tracking-widest">
                        <AlertTriangle size={14} /> Pending Sign-off
                    </div>
                    <ul className="mt-3 space-y-1 text-sm text-white/70">
                        {tiers.pending_signoff.map((s, i) => <li key={i}>• {s}</li>)}
                    </ul>
                </div>
            )}

            {/* Active markets / tiers */}
            <div className="bg-[#0A0B10] border border-white/10">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <div className="overline">CPL Engine — Cities & Micro-market Tiers</div>
                        <div className="text-xs text-white/40 mt-1">Read-only oversight. Flip a city's `active` flag in code (server.py CPL_CITY_CONFIG) once tiers are signed off.</div>
                    </div>
                    <button onClick={load} className="btn-outline-gold text-xs"><RefreshCw size={12} /> Refresh</button>
                </div>
                <div className="max-h-[420px] overflow-auto p-4 space-y-3">
                    {(tiers?.cities || []).map((c) => (
                        <details key={c.id} className="border border-white/10" open={c.active}>
                            <summary className="p-3 cursor-pointer flex items-center justify-between text-sm">
                                <div className="flex items-center gap-3">
                                    <span className={`h-2 w-2 rounded-full ${c.active ? "bg-[#D4AF37]" : "bg-white/20"}`} />
                                    <span className="font-serif-display text-lg">{c.name}</span>
                                    <span className="text-[10px] uppercase tracking-widest text-white/40">×{c.multiplier}</span>
                                </div>
                                <span className={`text-[10px] px-2 py-1 border ${c.active ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-white/50"}`}>
                                    {c.active ? "ACTIVE" : "HELD BACK"}
                                </span>
                            </summary>
                            <div className="p-3 border-t border-white/5 grid sm:grid-cols-2 gap-2 text-xs">
                                {(tiers?.micro_markets || []).filter((m) => m.city === c.name).map((m) => (
                                    <div key={m.id} className="flex items-center justify-between border border-white/5 p-2">
                                        <span className="text-white/70">{m.name}</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 border ${m.tier ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-white/10 text-white/40"}`}>
                                            {m.tier || "Unclassified → Established (Low)"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    ))}
                </div>
            </div>

            {/* Verified projects / feedback loop */}
            <div className="bg-[#0A0B10] border border-white/10">
                <div className="p-4 border-b border-white/10">
                    <div className="overline">Verified Campaign Projects — Feedback Loop</div>
                    <div className="text-xs text-white/40 mt-1">Update actual campaign results as new data comes in.</div>
                </div>
                <div className="max-h-[500px] overflow-auto p-4 space-y-3">
                    {projects.map((p) => (
                        <div key={p.id} className="border border-white/10 p-3 text-xs">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-serif-display text-base text-white">{p.project_name} <span className="text-white/40">— {p.builder}</span></span>
                                <span className="text-white/40">{p.city} · {p.micro_market} · {p.property_type}</span>
                            </div>
                            <div className="grid sm:grid-cols-4 gap-2">
                                <LabeledInput label="Actual CPL (₹)" defaultValue={p.actual_cpl}
                                    onChange={(v) => setEdits((e) => ({ ...e, [p.id]: { ...e[p.id], actual_cpl: Number(v) } }))} />
                                <LabeledInput label="Site visits" defaultValue={p.site_visits}
                                    onChange={(v) => setEdits((e) => ({ ...e, [p.id]: { ...e[p.id], site_visits: Number(v) } }))} />
                                <LabeledInput label="Closures" defaultValue={p.closures}
                                    onChange={(v) => setEdits((e) => ({ ...e, [p.id]: { ...e[p.id], closures: Number(v) } }))} />
                                <button onClick={() => saveProject(p)} disabled={!edits[p.id]} className="btn-gold text-[10px] justify-center disabled:opacity-40">
                                    <Save size={11} /> Save
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Calculation log */}
            <div className="bg-[#0A0B10] border border-white/10">
                <div className="p-4 border-b border-white/10">
                    <div className="overline">Recent Calculation Log</div>
                    <div className="text-xs text-white/40 mt-1">Every estimate, its weights and confidence. Retained 24 months, then auto-purged.</div>
                </div>
                <div className="max-h-[400px] overflow-auto p-4 space-y-2 text-xs">
                    {logs.length === 0 && <div className="text-white/40">No estimates yet.</div>}
                    {logs.map((l) => (
                        <div key={l.id} className="flex items-center justify-between border border-white/5 p-2">
                            <span className="text-white/60">{l.inputs.city} · {l.inputs.micro_market} · {l.inputs.property_type} · {l.inputs.lead_tier} · {l.inputs.lead_count} leads</span>
                            <span className="text-[#D4AF37]">₹{l.output.cpl_low}–{l.output.cpl_high} · {l.confidence}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function LabeledInput({ label, defaultValue, onChange }) {
    return (
        <label className="block">
            <span className="text-[9px] uppercase tracking-widest text-white/40">{label}</span>
            <input type="number" defaultValue={defaultValue ?? ""} onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full bg-black/50 border border-white/10 px-2 py-1.5 text-white text-xs" />
        </label>
    );
}
